/**
 * The components every CIDER surface uses. Plain React, no state library, no network.
 * Styles live in `components.css`, keyed by `ui-*` classes on the design tokens.
 */
import { useState } from 'react';
import { formatCount } from '../money.js';
import { formatCalendarDay } from '../time.js';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';

// ---- Button ----------------------------------------------------------------------------------

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary is the one red button on a screen; quiet is for the rest. danger asks for a reason. */
  /** `secondary` is the outline: every action that is not the screen's one primary (walk fixes 2, item 13). */
  kind?: 'primary' | 'secondary' | 'quiet' | 'danger';
  busy?: boolean;
}

/**
 * Walk fixes 2, item 13: an action that is not the screen's primary is an outlined button, never
 * a bare word. Until the canvas pass sets the rule, the default is the outline — `quiet` is left
 * for the few places a button really is a word inside a sentence.
 */
export function Button({
  kind = 'secondary',
  busy = false,
  className,
  children,
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      type={rest.type ?? 'button'}
      {...rest}
      className={['ui-button', `ui-button--${kind}`, className].filter(Boolean).join(' ')}
      disabled={rest.disabled === true || busy}
      aria-busy={busy ? true : undefined}
    >
      {children}
    </button>
  );
}

// ---- Chip --------------------------------------------------------------------------------------

/** `red` is E06's OVER and a gap — solid red, white word; `bad` is HARD STOP's red on pink. */
export type ChipKind = 'no-data' | 'sample' | 'ok' | 'warn' | 'bad' | 'info' | 'mute' | 'red';

export interface ChipProps {
  kind?: ChipKind;
  children?: ReactNode;
  title?: string;
}

/** The status chip. `NO DATA` and `SAMPLE` are the honest ones: a number never pretends. */
export function Chip({ kind = 'mute', children, title }: ChipProps): ReactNode {
  const text = children ?? (kind === 'no-data' ? 'NO DATA' : kind === 'sample' ? 'SAMPLE' : null);
  return (
    <span className={`ui-chip ui-chip--${kind}`} title={title}>
      {text}
    </span>
  );
}

/** Shorthand for the two chips a truth-first screen uses most. */
export const NoData = (): ReactNode => <Chip kind="no-data" />;
export const Sample = (): ReactNode => <Chip kind="sample" title="Sample data — not this year's" />;

// ---- Table -------------------------------------------------------------------------------------

export interface Column<T> {
  key: string;
  head: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-align numbers. */
  num?: boolean;
  /** Chips sit to the right; the row's "…" menu is the last column. */
  align?: 'chips' | 'menu';
}
const cellClass = <T,>(c: Column<T>): string | undefined =>
  c.num
    ? 'ui-table__num'
    : c.align === 'chips'
      ? 'ui-table__chips'
      : c.align === 'menu'
        ? 'ui-table__menu'
        : undefined;

export interface TableProps<T> {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** What the table says when it has no rows. Never an empty box. */
  empty: ReactNode;
  caption?: string;
}

export function Table<T>({ columns, rows, rowKey, empty, caption }: TableProps<T>): ReactNode {
  return (
    <div className="ui-table-wrap">
      <table className="ui-table">
        {caption ? <caption>{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={cellClass(c)}>
                {c.head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="ui-table__empty">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={rowKey(r)}>
                {columns.map((c) => (
                  <td key={c.key} className={cellClass(c)}>
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---- Forms -------------------------------------------------------------------------------------

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
  id: string;
}

/** One labeled control with its hint and its error, in that order, always. */
/**
 * **The shared date control** (James, Oct 1 — B5). The date in the field, and beneath it the day
 * it falls on in words — "Fri, Sep 11, 2026" — so the weekday is read before it is saved. A bare
 * browser date box said nothing about the day, and a festival Friday went in as a Thursday.
 */
export function DateField({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string | undefined;
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  const day = /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatCalendarDay(value) : null;
  const says = [day, hint].filter((x): x is string => !!x).join(' · ');
  return (
    <Field id={id} label={label} {...(says ? { hint: says } : {})}>
      <Input
        id={id}
        type="date"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </Field>
  );
}

export function Field({ label, hint, error, children, id }: FieldProps): ReactNode {
  return (
    <div className={['ui-field', error ? 'ui-field--error' : ''].join(' ')}>
      <label htmlFor={id} className="ui-field__label">
        {label}
      </label>
      {children}
      {hint ? (
        <div id={`${id}-hint`} className="ui-field__hint">
          {hint}
        </div>
      ) : null}
      {error ? (
        <div id={`${id}-error`} className="ui-field__error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>): ReactNode {
  return <input {...props} className={['ui-input', props.className].filter(Boolean).join(' ')} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): ReactNode {
  return <select {...props} className={['ui-input', props.className].filter(Boolean).join(' ')} />;
}

export interface FormProps {
  onSubmit: () => void;
  children: ReactNode;
  /** The one sentence that says why the form refused, from the API. */
  refusal?: string | null;
  busy?: boolean;
}

/**
 * A form. A required field left empty is said in CIDER's own sentence, naming the field —
 * never the browser's bubble, never silence (the rule for every drawn action).
 */
export function Form({ onSubmit, children, refusal, busy }: FormProps): ReactNode {
  const [missing, setMissing] = useState<string | null>(null);
  return (
    <form
      className="ui-form"
      noValidate
      aria-busy={busy === true ? true : undefined}
      onSubmit={(e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const first = [...form.elements].find(
          (el): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
            (el instanceof HTMLInputElement ||
              el instanceof HTMLSelectElement ||
              el instanceof HTMLTextAreaElement) &&
            !el.checkValidity(),
        );
        if (first) {
          const label =
            (first.id ? form.querySelector(`label[for="${first.id}"]`)?.textContent : null) ??
            first.getAttribute('aria-label') ??
            'a field';
          setMissing(
            first.validity.valueMissing
              ? `${label.trim()} is needed first.`
              : `${label.trim()}: ${first.validationMessage}`,
          );
          first.focus();
          return;
        }
        setMissing(null);
        onSubmit();
      }}
    >
      {children}
      {(refusal ?? missing) ? (
        <div className="ui-form__refusal" role="alert">
          {refusal ?? missing}
        </div>
      ) : null}
    </form>
  );
}

// ---- Toast -------------------------------------------------------------------------------------

export interface ToastItem {
  id: string;
  kind: 'ok' | 'warn' | 'bad' | 'info';
  text: string;
}

/** The toast stack. The app owns the list; this only draws it. */
export function Toasts({
  items,
  onClose,
}: {
  items: readonly ToastItem[];
  onClose: (id: string) => void;
}): ReactNode {
  if (items.length === 0) return null;
  return (
    <div className="ui-toasts" role="region" aria-label="Notices">
      {items.map((t) => (
        <div
          key={t.id}
          className={`ui-toast ui-toast--${t.kind}`}
          role={t.kind === 'bad' ? 'alert' : 'status'}
        >
          <span>{t.text}</span>
          <button
            type="button"
            className="ui-toast__close"
            aria-label="Dismiss"
            onClick={() => {
              onClose(t.id);
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

// ---- Empty state -------------------------------------------------------------------------------

export interface EmptyProps {
  title: string;
  /** What is missing and where it will come from. Never "nothing here". */
  says: string;
  chip?: ChipKind;
}

export function Empty({ title, says, chip = 'no-data' }: EmptyProps): ReactNode {
  return (
    <div className="ui-empty">
      <div className="ui-empty__title">
        {title} <Chip kind={chip} />
      </div>
      <p className="ui-empty__says">{says}</p>
    </div>
  );
}

// ---- The Sep 21 design pass: the pieces every screen is laid on ---------------------------------

/** One NO DATA sentence in plain words, where a list or a card has nothing. */
export function EmptyState({ says }: { says: string }): ReactNode {
  return (
    <p className="ui-empty-line">
      <Chip kind="no-data" /> {says}
    </p>
  );
}

/**
 * The dialog: every create, edit and end action opens one — a title, one sentence of charter,
 * the fields, and Cancel. It scrolls inside itself on a phone. A dialog that only says
 * something (a way in that is drawn and not built yet) is closed, not canceled: it passes
 * `closeLabel="Close"`, because there is nothing to call off.
 */
export function Dialog({
  title,
  charter,
  onClose,
  closeLabel = 'Cancel',
  children,
}: {
  title: string;
  charter?: string;
  onClose: () => void;
  closeLabel?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="ui-dialog" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ui-dialog__card">
        <h1>{title}</h1>
        {charter ? <p className="charter">{charter}</p> : null}
        {children}
        <p className="ui-dialog__foot">
          <Button type="button" onClick={onClose}>
            {closeLabel}
          </Button>
        </p>
      </div>
    </div>
  );
}

/** Two columns on a desk, one on a phone; a label never wraps. A `wide` child spans both. */
export function FieldGrid({ children }: { children: ReactNode }): ReactNode {
  return <div className="ui-field-grid">{children}</div>;
}

/** The address as one block: street, then city · state · ZIP on one line. */
export function AddressBlock({ children }: { children: ReactNode }): ReactNode {
  return <div className="ui-address">{children}</div>;
}
export function AddressRow({ children }: { children: ReactNode }): ReactNode {
  return <div className="ui-address__row">{children}</div>;
}

/** A row's "…" menu: the actions on the row, never a form beside the list. */
export interface MenuAction {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}
export function RowMenu({
  label,
  trigger = 'Actions',
  actions,
}: {
  /** What a screen reader hears: "Actions — Rosati's Pizza". */
  label: string;
  /** What the button says. One outlined word, never a bare "…" (walk fixes 2, items 11 and 13). */
  trigger?: string;
  actions: MenuAction[];
}): ReactNode {
  const [open, setOpen] = useState(false);
  if (actions.length === 0) return null;
  return (
    <span className="ui-menu">
      <Button
        type="button"
        kind="secondary"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        {trigger}
      </Button>
      {open ? (
        <span className="ui-menu__list" role="menu">
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              className={['ui-menu__item', a.danger ? 'ui-menu__item--danger' : ''].join(' ')}
              disabled={a.disabled}
              onClick={() => {
                setOpen(false);
                a.onClick();
              }}
            >
              {a.label}
            </button>
          ))}
        </span>
      ) : null}
    </span>
  );
}

/** Things beside each other: the header's buttons, a row of chips. */
export function Row({
  children,
  tight,
  start,
  between,
  end,
}: {
  children: ReactNode;
  tight?: boolean;
  start?: boolean;
  between?: boolean;
  end?: boolean;
}): ReactNode {
  return (
    <div
      className={[
        'ui-row',
        tight ? 'ui-row--tight' : '',
        start ? 'ui-row--start' : '',
        between ? 'ui-row--between' : '',
        end ? 'ui-row--end' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}
/** Things under each other. */
export function Stack({ children, tight }: { children: ReactNode; tight?: boolean }): ReactNode {
  return <div className={tight ? 'ui-stack ui-stack--tight' : 'ui-stack'}>{children}</div>;
}

// ---- The standing rule ---------------------------------------------------------------------

export interface WarningProps {
  /** What is wrong, in CIDER's own words — from the API, never written on the screen. */
  says: string;
  /** False for one of the five hard stops: then there is no way past, and the law is named. */
  canProceed: boolean;
  /** The law that makes it a refusal, and who can settle it. Only on a hard stop. */
  law?: string | null;
  whoToCall?: string | null;
  /** The words on the button, where "Proceed anyway" is not them. */
  proceedSays?: string;
  /** Called with the one-line reason. The caller sends the same request again with it. */
  onProceed: (reason: string) => void;
  busy?: boolean;
}

/**
 * The standing rule on the screen (item 7): **CIDER warns and lets a person proceed.**
 *
 * The sentence says what is wrong. Under it, a box for one line in their own words and a
 * button. Pressing it sends the same request again with the reason, and the record carries
 * "Proceeded by Dan Kowalski · 'Vendor's truck is here'" from then on.
 *
 * Where `canProceed` is false this is one of the five in
 * `packages/domain/src/access/hard_stops.ts`: no box, no button, the law named, and who to
 * call — a refusal is never a dead end either.
 */
export function Warning({
  says,
  canProceed,
  law,
  whoToCall,
  proceedSays = 'Proceed anyway',
  onProceed,
  busy,
}: WarningProps): ReactNode {
  const [reason, setReason] = useState('');
  return (
    <div className={`ui-warn${canProceed ? '' : ' ui-warn--stop'}`} role="alert">
      <div className="ui-warn__says">{says}</div>
      {canProceed ? (
        <div className="ui-warn__past">
          <Field label="Why you are going ahead" id="ui-warn-reason">
            <Input
              id="ui-warn-reason"
              value={reason}
              maxLength={200}
              placeholder="Vendor’s truck is here"
              onChange={(e) => {
                setReason(e.currentTarget.value);
              }}
            />
          </Field>
          <Button
            busy={busy ?? false}
            disabled={reason.trim().length < 3}
            onClick={() => {
              onProceed(reason.trim());
            }}
          >
            {proceedSays}
          </Button>
        </div>
      ) : (
        <div className="ui-warn__law">
          {law ? <span className="ui-warn__lawName">{law}</span> : null}
          {whoToCall ? <span> · ask {whoToCall}</span> : null}
        </div>
      )}
    </div>
  );
}

// ---- Show inactive -----------------------------------------------------------------------------

/**
 * **Inactive rows are hidden by default, on every list** (James, Oct 20). One rule for anything a
 * row can be — deactivated, archived, ended, retired, turned off or marked junk: the list shows
 * the active rows, and one checkbox at its top reads "Show inactive (N)". Checked, the inactive
 * rows appear in place with their own tags and buttons. The choice sticks per list in this
 * browser. Nothing is deleted and no data changes — it is display only, and a direct link still
 * opens an inactive row.
 */
export function useShowInactive(list: string): [boolean, (on: boolean) => void] {
  const key = `cider.showInactive.${list}`;
  const [on, setOn] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  return [
    on,
    (next: boolean) => {
      setOn(next);
      try {
        window.localStorage.setItem(key, next ? '1' : '0');
      } catch {
        // A private window keeps the choice for this visit only.
      }
    },
  ];
}

/** The rows a list shows, its active count for the count line, and the inactive count for the box. */
export function splitInactive<T>(
  rows: readonly T[],
  inactive: (row: T) => boolean,
  show: boolean,
): { rows: T[]; active: number; inactive: number } {
  const off = rows.filter(inactive).length;
  return {
    rows: show ? [...rows] : rows.filter((r) => !inactive(r)),
    active: rows.length - off,
    inactive: off,
  };
}

/**
 * The one checkbox: "Show inactive (N)", unchecked by default. With nothing inactive it is not
 * drawn — a box that shows nothing is a control with no act behind it (CLAUDE.md, Fidelity).
 */
export function ShowInactive({
  id,
  count,
  on,
  onChange,
}: {
  id: string;
  count: number;
  on: boolean;
  onChange: (on: boolean) => void;
}): ReactNode {
  if (count === 0 && !on) return null;
  return (
    <label className="ui-check ui-show-inactive" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={on}
        onChange={(e) => {
          onChange(e.currentTarget.checked);
        }}
      />
      Show inactive ({formatCount(count)})
    </label>
  );
}
